import React, { useEffect, useRef, useState } from 'react';
import './AdminUsersPage.scss';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';
import {
  getUsersPage,
  resetPasswordByAdmin,
  getApiClients,
  BillingCycleFilter,
} from '../../../shared/services/admin/users/AdminUsersService';
import { UserDataResponse } from '../../../shared/models/admin/users/UserDataResponse';
import Loader from '../../../shared/components/common/loader/Loader';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog, ConfirmDialogReturn } from 'primereact/confirmdialog';
import AdminRegisterUser from './components/admin-register-user/AdminRegisterUser';
import AdminFiltersPopover from './components/admin-filters-popover/AdminFiltersPopover';
import { formatBilling } from '../../../shared/helpers/admin/formatBilling';
import { NavLink, useNavigate } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { format } from 'date-fns';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import { PROJECTS } from '../../../shared/routes/path';
import { useAdminUser } from '../../../shared/contexts/admin/user/AdminUserContext';
import icoInfo from '../../../assets/icons/ico-info.svg';
import { AccountType } from '../../../shared/types/account/AccountType';
import { AccountStatus } from '../../../shared/types/account/AccountStatus';
import { PaginationData } from '../../../shared/models/monitor/data/sources/PaginationData';
import {
  calculatePaginationValues,
  createPaginationHandler,
  calculateSafeFirst,
} from '../../../shared/helpers/PaginationHelper';

type AccountOriginFilter =
  | 'all'
  | 'organic'
  | 'cs_assisted'
  | 'cs_assisted_brand24'
  | `api_client_${number}`;

const AdminUsersPage: React.FC = () => {
  const { t } = useTranslation();
  const { setSelectedUserData, selectedUserData } = useAdminUser();
  const navigate = useNavigate();
  let confirmRef: ConfirmDialogReturn;

  const [canceledFilter, setCanceledFilter] = useState<'all' | 'canceled' | 'notCanceled'>('all');
  const [confirmedFilter, setConfirmedFilter] = useState<'all' | 'confirmed' | 'unconfirmed'>('all');
  const [apiClients, setApiClients] = useState<{ id: number; name: string; accountCount: number }[]>([]);
  const [accountOriginFilter, setAccountOriginFilter] = useState<AccountOriginFilter>('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState<AccountType | 'all'>('all');
  const [accountStatusFilter, setAccountStatusFilter] = useState<AccountStatus | 'all'>('all');
  const [billingCycleFilter, setBillingCycleFilter] = useState<BillingCycleFilter>('all');
  const [noProjectFilter, setNoProjectFilter] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC' | undefined>(undefined);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState<PaginationData | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [totalAllAccounts, setTotalAllAccounts] = useState<number | undefined>();
  const [totalFilteredAccounts, setTotalFilteredAccounts] = useState<number | undefined>();
  const [accountOriginCounts, setAccountOriginCounts] = useState({
    all: 0,
    organic: 0,
    csAssisted: 0,
    csAssistedBrand24: 0,
  });

  const hideDialog = () => {
    if (confirmRef) {
      confirmRef.hide();
    }

    refresh();
  };

  const addNewAccount = () => {
    confirmRef = confirmDialog({
      message: <AdminRegisterUser afterSave={hideDialog} />,
      header: 'Add new account',
      defaultFocus: 'accept',
      acceptClassName: 'confirm-change-name-btn',
      footer: () => null,
    });
  };

  const showResetPasswordResult = (email: string, isOk: boolean) => {
    const header = isOk ? 'Success' : 'Error';
    const message = isOk
      ? `Password reset request has been sent to ${email}`
      : 'Error resetting password. Please try again later.';

    confirmDialog({
      message,
      header,
      defaultFocus: 'accept',
      rejectClassName: 'hide',
      acceptLabel: 'OK',
    });
  };

  const showConfirmReset = (email: string) => {
    confirmDialog({
      message: 'Are you sure you want to reset password for this user?',
      header: 'Are you sure?',
      defaultFocus: 'accept',
      accept: () => resetPassword(email),
    });
  };

  const resetPassword = (email: string) => {
    resetPasswordByAdmin(email)
      .then(() => showResetPasswordResult(email, true))
      .catch(() => showResetPasswordResult(email, false));
  };

  const refresh = async () => {
    setIsLoading(true);
    return getUsersPage({
      search: debouncedSearch || undefined,
      accountOrigin: accountOriginFilter,
      accountType: accountTypeFilter !== 'all' ? accountTypeFilter : undefined,
      accountStatus: accountStatusFilter !== 'all' ? accountStatusFilter : undefined,
      isCancelled: canceledFilter !== 'all' ? canceledFilter === 'canceled' : undefined,
      confirmed: confirmedFilter !== 'all' ? confirmedFilter === 'confirmed' : undefined,
      noProject: noProjectFilter || undefined,
      billingInterval: billingCycleFilter !== 'all' ? billingCycleFilter : undefined,
      sortBy,
      sortDir,
      page,
      pageSize,
    })
      .then((response) => {
        setSelectedUserData(response.data);
        setPagination(response.pagination);
        setTotalAllAccounts(response.totalAllAccounts);
        setTotalFilteredAccounts(response.totalFilteredAccounts);
        setAccountOriginCounts(response.accountOriginCounts);
        setIsLoading(false);
        setInitialLoad(false);
      })
      .catch(() => {
        setIsLoading(false);
        setInitialLoad(false);
      });
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchValue), 400);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Single source of truth for "fetch the list". Filter changes reset to
  // page 1 in the same render, so we don't double-fetch (stale page → page 1
  // would trigger the fetch effect twice). Sort and pageSize don't reset.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      refresh();
      return;
    }
    if (page !== 1) {
      setPage(1);
      return;
    }
    refresh();
  }, [
    canceledFilter,
    confirmedFilter,
    accountOriginFilter,
    accountTypeFilter,
    accountStatusFilter,
    billingCycleFilter,
    noProjectFilter,
    debouncedSearch,
  ]);

  // Page and sort changes refetch directly without the page-reset short-circuit.
  useEffect(() => {
    if (isFirstRender.current) return;
    refresh();
  }, [page, pageSize, sortBy, sortDir]);

  // api_clients counts depend on the four filters that affect the underlying
  // grouping in `getApiClients` — only refetch when those change.
  useEffect(() => {
    getApiClients({
      search: debouncedSearch || undefined,
      accountType: accountTypeFilter !== 'all' ? accountTypeFilter : undefined,
      accountStatus: accountStatusFilter !== 'all' ? accountStatusFilter : undefined,
      isCancelled: canceledFilter !== 'all' ? canceledFilter === 'canceled' : undefined,
    }).then(setApiClients);
  }, [canceledFilter, confirmedFilter, accountTypeFilter, accountStatusFilter, debouncedSearch]);

  if (isLoading && initialLoad) {
    return (
      <div className="settings">
        <Loader />
      </div>
    );
  }

  const actionBodyTemplate = (data: UserDataResponse) => {
    return (
      <div className="table__cell--btns">
        <Button className="action-btn" onClick={() => navigate(`/admin/accounts/${data.accountId}`)}>
          <span className="ico--edit ico--xs" />
        </Button>
        <Button className="action-btn" onClick={() => showConfirmReset(data.email)}>
          <span className="ico--password ico--xs" />
        </Button>
      </div>
    );
  };

  const firstProjectTemplate = (data: UserDataResponse) => {
    return (
      <div className="admin-users__project-name">
        {data.projects.length ? (
          <NavLink to={`${PROJECTS}/${data.projects[0].id}`} target="_blank">
            {data.projects[0].label}
          </NavLink>
        ) : (
          '-'
        )}
      </div>
    );
  };

  const renderSearch = () => {
    return (
      <div className="page-box__buttons" style={{ marginRight: '7px' }}>
        <div className="input-search-box">
          <InputText
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search..."
            className="input-search"
          />
        </div>
      </div>
    );
  };

  const emptyTemplate = () => {
    return (
      <div className="no-results">
        <div className="no-results__icon material-symbols-outlined">search</div>
        <div className="no-results__title">{t('noResultsFound')}</div>
        <div className="no-results__desc">{t('noResultsFoundDesc')}</div>
      </div>
    );
  };


  const limitTemplateBody = (data: UserDataResponse) => {
    if (data.accountType === 'PAID') {
      return <span>{data.monitorCount + ' / unlimited'}</span>;
    }

    if (data.maxMonitorCount > data.monitorCount) {
      return <span>{data.monitorCount + ' / ' + data.maxMonitorCount}</span>;
    }

    return <span style={{ color: 'red' }}>{data.monitorCount + ' / ' + data.maxMonitorCount}</span>;
  };

  const emailTemplate = (data: UserDataResponse) => {
    const isSignup =
      data.source === 'SIGNUP_PAGE' || data.source === 'GOOGLE' || data.source === 'MAGIC_LINK';
    return (
      <div className={`${getClass(data)} user-email`}>
        <span
          className={`admin-users__confirmed-dot admin-users__confirmed-dot--${
            data.confirmed ? 'yes' : 'no'
          }`}
        />
        <NavLink to={`/admin/accounts/${data.accountId}`}>{data.email}</NavLink>
        {isSignup && (
          <>
            <CustomTooltip tooltipText="Self-sign-up" iconClass="ico--star" />
          </>
        )}
      </div>
    );
  };

  const getClass = (row: UserDataResponse) => {
    if (row.accountStatus === 'BLOCKED') {
      return 'table__cell--blocked';
    }

    if (row.accountStatus === 'DISABLED') {
      return 'table__cell--disabled';
    }

    if (row.accountStatus === 'OVERDUE') {
      return 'table__cell--overdue';
    }

    if (row.accountStatus === 'PAID') {
      return 'table__cell--paid';
    }

    return '';
  };

  const accountOriginOptions = [
    { label: `${t('allAccounts')} (${accountOriginCounts.all})`, value: 'all' },
    { label: `${t('organicAccounts')} (${accountOriginCounts.organic})`, value: 'organic' },
    { label: `${t('csAssistedAccounts')} (${accountOriginCounts.csAssisted})`, value: 'cs_assisted' },
    {
      label: `${t('csAssistedBrand24Accounts')} (${accountOriginCounts.csAssistedBrand24})`,
      value: 'cs_assisted_brand24',
    },
    ...(apiClients.length ? [{ label: '', value: '__sep__', disabled: true, separator: true }] : []),
    ...apiClients.map((c) => ({ label: `${c.name} (${c.accountCount})`, value: `api_client_${c.id}` })),
  ];

  const apiClientItemTemplate = (option: any) => {
    if (option.separator) {
      return <div className="admin-users__dropdown-separator" />;
    }
    return <span>{option.label}</span>;
  };

  const accountOriginValueTemplate = (option: any) => {
    if (!option) {
      return <span>{t('allAccounts')}</span>;
    }
    return <span>{option.label}</span>;
  };

  const accountTypeOptions = [
    { label: t('allAccountTypes'), value: 'all' },
    { label: 'Trial', value: 'TRIAL' },
    { label: 'Standard', value: 'STANDARD' },
    { label: 'Pro', value: 'PRO' },
    { label: 'Advanced', value: 'ADVANCED' },
    { label: 'Enterprise', value: 'ENTERPRISE' },
    { label: 'Business', value: 'BUSINESS' },
    { label: 'Essential', value: 'ESSENTIAL' },
    { label: 'Advanced V2', value: 'ADVANCED_V2' },
  ];

  const accountStatusOptions = [
    { label: t('allStatuses'), value: 'all' },
    { label: 'Paid', value: 'PAID' },
    { label: 'Trial', value: 'TRIAL' },
    { label: 'Overdue', value: 'OVERDUE' },
    { label: 'Blocked', value: 'BLOCKED' },
    { label: 'Disabled', value: 'DISABLED' },
  ];

  const billingCycleOptions: { label: string; value: BillingCycleFilter }[] = [
    { label: 'All cycles', value: 'all' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Annual', value: 'annual' },
  ];

  const limitCell = (used: number, max: number, isUnlimited = false) => {
    if (isUnlimited) {
      return <span>{`${used} / unlimited`}</span>;
    }
    if (max > used) {
      return <span>{`${used} / ${max}`}</span>;
    }
    return <span style={{ color: 'red' }}>{`${used} / ${max}`}</span>;
  };

  const { first, rows, totalRecords } = calculatePaginationValues(pagination, selectedUserData.length);
  const safeFirst = calculateSafeFirst(first, rows, totalRecords);

  return (
    <div className="admin-users">
      <ConfirmDialog className="dialog-box" />

      <div className="admin-users__action-panel">
        <div className="admin-users__filters">
          <Dropdown
            value={accountOriginFilter}
            options={accountOriginOptions}
            onChange={(e) => setAccountOriginFilter(e.value)}
            className="dropdown dropdown--btn-sm"
            itemTemplate={apiClientItemTemplate}
            valueTemplate={accountOriginValueTemplate}
          />
          <Dropdown
            value={accountTypeFilter}
            options={accountTypeOptions}
            onChange={(e) => setAccountTypeFilter(e.value)}
            className="dropdown dropdown--btn-sm"
            valueTemplate={(opt: any) => (
              <span>{opt?.value === 'all' ? t('accountType') : opt?.label}</span>
            )}
          />
          <Dropdown
            value={accountStatusFilter}
            options={accountStatusOptions}
            onChange={(e) => setAccountStatusFilter(e.value)}
            className="dropdown dropdown--btn-sm"
            valueTemplate={(opt: any) => (
              <span>{opt?.value === 'all' ? t('accountStatus') : opt?.label}</span>
            )}
          />
          <AdminFiltersPopover
            label="More filters"
            activeCount={
              (canceledFilter !== 'all' ? 1 : 0) +
              (confirmedFilter !== 'all' ? 1 : 0) +
              (accountOriginFilter !== 'all' ? 1 : 0) +
              (accountTypeFilter !== 'all' ? 1 : 0) +
              (accountStatusFilter !== 'all' ? 1 : 0) +
              (billingCycleFilter !== 'all' ? 1 : 0) +
              (noProjectFilter ? 1 : 0)
            }
            onReset={() => {
              setCanceledFilter('all');
              setConfirmedFilter('all');
              setAccountOriginFilter('all');
              setAccountTypeFilter('all');
              setAccountStatusFilter('all');
              setBillingCycleFilter('all');
              setNoProjectFilter(false);
              setSearchValue('');
              setSortBy(undefined);
              setSortDir(undefined);
            }}
          >
            <Dropdown
              value={canceledFilter}
              options={[
                { label: t('no'), value: 'all' },
                { label: t('yes'), value: 'canceled' },
              ]}
              onChange={(e) => setCanceledFilter(e.value)}
              className="dropdown dropdown--btn-sm"
              valueTemplate={(opt: any) => <span>{`${t('onlyCanceledAccounts')}: ${opt?.label}`}</span>}
            />
            <Dropdown
              value={confirmedFilter}
              options={[
                { label: 'All', value: 'all' },
                { label: t('confirmed'), value: 'confirmed' },
                { label: t('unconfirmed'), value: 'unconfirmed' },
              ]}
              onChange={(e) => setConfirmedFilter(e.value)}
              className="dropdown dropdown--btn-sm"
              valueTemplate={(opt: any) => <span>{`${t('confirmed')}: ${opt?.label}`}</span>}
            />
            <Dropdown
              value={billingCycleFilter}
              options={billingCycleOptions}
              onChange={(e) => setBillingCycleFilter(e.value)}
              className="dropdown dropdown--btn-sm"
              valueTemplate={(opt: any) => (
                <span>{opt?.value === 'all' ? 'Billing cycle' : `Billing: ${opt?.label}`}</span>
              )}
            />
            <Dropdown
              value={noProjectFilter ? 'noProject' : 'all'}
              options={[
                { label: 'All projects', value: 'all' },
                { label: 'Without projects', value: 'noProject' },
              ]}
              onChange={(e) => setNoProjectFilter(e.value === 'noProject')}
              className="dropdown dropdown--btn-sm"
              valueTemplate={(opt: any) => (
                <span>{opt?.value === 'all' ? 'Projects' : opt?.label}</span>
              )}
            />
          </AdminFiltersPopover>
        </div>
        <div className="admin-users__action-panel-right">
          {renderSearch()}
          <Button
            className="btn btn--sm"
            label="Add new account"
            onClick={() => addNewAccount()}
            style={{ marginRight: '7px' }}
          />
        </div>
      </div>
      <DataTable
        lazy
        paginator
        scrollable
        paginatorClassName="pagination"
        stripedRows
        removableSort
        value={selectedUserData}
        rows={rows}
        first={safeFirst}
        totalRecords={totalRecords}
        sortField={sortBy}
        sortOrder={sortDir === 'ASC' ? 1 : sortDir === 'DESC' ? -1 : 0}
        onSort={(e) => {
          setSortBy(typeof e.sortField === 'string' ? e.sortField : undefined);
          setSortDir(e.sortOrder === 1 ? 'ASC' : e.sortOrder === -1 ? 'DESC' : undefined);
        }}
        onPage={createPaginationHandler(pagination, (newPage, newPageSize) => {
          setPage(newPage);
          setPageSize(newPageSize);
        })}
        rowsPerPageOptions={[10, 25, 50]}
        tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
        tableClassName="table"
        className="table-wrapper"
        emptyMessage={emptyTemplate}
        currentPageReportTemplate={t('rowsNumber') + ':'}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      >
        <Column
          field="accountId"
          header="User ID"
          sortable
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '60px' }}
        />
        <Column
          field="email"
          header="User name"
          sortable
          body={emailTemplate}
          headerClassName="table__header"
          style={{ minWidth: '260px' }}
        />
        <Column
          header="First project"
          body={firstProjectTemplate}
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '160px' }}
        />
        <Column
          field="created"
          header="Created"
          sortable
          body={(data) => (data.created ? format(data.created, 'yyyy-MM-dd') : '-')}
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '110px' }}
        />
        <Column
          field="endsAt"
          header="End date"
          sortable
          body={(data) => (data.accountEndAt ? format(data.accountEndAt, 'yyyy-MM-dd') : '-')}
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '110px' }}
        />
        <Column
          field="lastLogin"
          header="Last login"
          sortable
          body={(data) => (data.lastLogin ? format(data.lastLogin, 'yyyy-MM-dd') : '-')}
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '110px' }}
        />
        <Column
          field="loginCount"
          header="Logins"
          sortable
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '80px' }}
        />
        <Column
          field="accountType"
          header="Account type"
          sortable
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '110px' }}
        />
        <Column
          field="billingInterval"
          header="Billing"
          sortable
          body={(data: UserDataResponse) => formatBilling(data.billingInterval)}
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '90px' }}
        />
        <Column
          field="projectsCount"
          header="Projects"
          sortable
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          body={(data: UserDataResponse) => <span>{data.projectsCount}</span>}
          style={{ width: '90px' }}
        />
        <Column
          field="monitorCount"
          header="Prompts"
          sortable
          body={limitTemplateBody}
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '120px' }}
        />
        <Column
          field="usersCount"
          header="Users"
          sortable
          body={(data: UserDataResponse) => limitCell(data.usersCount, data.maxUsers)}
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '100px' }}
        />
        <Column
          field="invitedUsersCount"
          header="Invited"
          sortable
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '80px' }}
        />
        <Column
          field="discount"
          header="Discount"
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '90px' }}
        />
        <Column
          field="paymentSuspended"
          header={t('paymentSuspended')}
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          body={(data) =>
            data.paymentSuspended ? (
              <img src={icoInfo} alt={t('paymentSuspended')} width={16} height={16} />
            ) : null
          }
          style={{ width: '60px' }}
        />
        <Column
          field="isCancelled"
          header="Cancelled at"
          body={(data: UserDataResponse) => {
            if (!data.isCancelled) return '-';
            const d = new Date(String(data.isCancelled));
            return isNaN(d.getTime()) ? '-' : format(d, 'yyyy-MM-dd');
          }}
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '110px' }}
          hidden={canceledFilter !== 'canceled'}
        />
        <Column
          frozen={true}
          alignFrozen="right"
          header="Actions"
          body={actionBodyTemplate}
          headerClassName="table__header table__header--center"
          bodyClassName={(data) => `${getClass(data)} table__cell--center`}
          style={{ width: '80px' }}
        />
      </DataTable>
    </div>
  );
};

export default AdminUsersPage;
